import type { ReactNode } from 'react';

import { MonoLabel, cn } from '@/design-system';
import { organisationInitials, type Organisation } from '@/entities/order';

import { useOrganisation } from './useOrganisation';

/**
 * De schil van elk kantoorscherm: een donkere kopbalk met de actieve
 * omgeving, een eyebrow, de titel en de acties, daaronder een scrollend
 * werkvlak.
 *
 * Vijf schermen gebruiken deze schil, dus hij staat in `shared/ui` en niet in
 * een feature.
 */
export type ScreenHeaderVariant = 'standard' | 'mid';

export interface ScreenHeaderProps {
    /** Mono, hoofdletters: "142 KEURINGEN · 7 VRAGEN AANDACHT". */
    eyebrow: string;
    title: string;
    /** Hoogstens één `primary` knop per kopbalk. */
    actions?: ReactNode;
    variant?: ScreenHeaderVariant;
    /** Alleen meegeven om de omgeving te overschrijven (tests, previews). */
    organisation?: Organisation | null;
}

export function ScreenHeader({
    eyebrow,
    title,
    actions,
    variant = 'standard',
    organisation,
}: ScreenHeaderProps) {
    const mid = variant === 'mid';

    return (
        <header
            /*
             * Ook de standaardkop breekt af. Zonder `flex-wrap` werd op een telefoon de
             * titel platgedrukt tot één letter en gingen de knoppen eroverheen; nu valt de
             * knoppenrij op zijn eigen regel, precies zoals bij `mid`. Op een breed scherm
             * verandert er niets: daar past alles naast elkaar.
             */
            className={cn(
                'flex shrink-0 flex-wrap justify-between bg-[var(--ink-800)] [row-gap:12px]',
                mid ? 'items-center gap-[18px] px-6 py-4' : 'items-end gap-5 px-7 py-5',
            )}
        >
            <div className="min-w-0">
                <ScreenEyebrow
                    text={eyebrow}
                    organisation={organisation}
                    className={mid ? 'mb-[5px]' : 'mb-[6px]'}
                />
                <h1
                    className={cn(
                        'm-0 font-[family-name:var(--font-display)] font-semibold tracking-[-0.02em] text-[var(--text-on-dark)]',
                        // De maat staat vóór de regelhoogte: tailwind-merge ziet
                        // `text-[…]` en `leading-[…]` als hetzelfde conflict en
                        // gooit anders de regelhoogte weg.
                        mid ? 'text-[22px] leading-[1.15]' : 'text-[24px] leading-[1.15]',
                    )}
                >
                    {title}
                </h1>
            </div>
            {actions ? (
                <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>
            ) : null}
        </header>
    );
}

export interface ScreenEyebrowProps {
    /** Mono, hoofdletters; de naam van de omgeving komt er vanzelf voor. Leeg: alleen die naam. */
    text: string;
    /** Alleen meegeven om de omgeving te overschrijven (tests, previews). */
    organisation?: Organisation | null;
    className?: string;
}

/**
 * De eyebrow van een donkere kop: het merkteken en de naam van de omgeving,
 * dan de tekst. Elke kop in de app begint zo, ook die van een keuring, zodat je
 * altijd ziet in welk bedrijf je werkt.
 */
export function ScreenEyebrow({ text, organisation, className }: ScreenEyebrowProps) {
    const current = useOrganisation();
    const active = organisation === undefined ? current : organisation;

    return (
        <div className={cn('flex min-w-0 items-center gap-2', className)}>
            {active ? <TenantMark organisation={active} /> : null}
            <MonoLabel size={10} tone="onDarkMuted" as="div" className="truncate tracking-[0.1em]">
                {active && text !== '' ? `${active.name} · ${text}` : (active?.name ?? text)}
            </MonoLabel>
        </div>
    );
}

/**
 * Het merkteken van de actieve omgeving. Nooit in de huisstijlkleur van de
 * klant: die kleurt het rapport, niet de app (patterns §10).
 */
function TenantMark({ organisation }: { organisation: Organisation }) {
    if (organisation.logo_url) {
        return (
            <img
                src={organisation.logo_url}
                alt=""
                width={20}
                height={20}
                className="h-5 w-5 shrink-0 rounded-[5px] bg-[var(--surface-card)] object-contain"
            />
        );
    }

    return (
        <span
            aria-hidden="true"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] bg-[var(--ink-700)] font-[family-name:var(--font-mono)] text-[9px] leading-none text-[var(--text-on-dark-2)]"
        >
            {organisationInitials(organisation.name)}
        </span>
    );
}

/** Het scrollende werkvlak onder de kopbalk. */
export function ScreenBody({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            className={cn(
                'min-h-0 flex-1 overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8',
                className,
            )}
        >
            {children}
        </div>
    );
}

/**
 * Kopbalk + werkvlak samen. Het scherm vult de hoogte van het werkvlak van de
 * shell (`flex: 1; min-width: 0`) en bepaalt zélf waar er gescrold wordt: de
 * pagina scrolt nooit mee.
 */
export function Screen({ children }: { children: ReactNode }) {
    return <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">{children}</div>;
}
